/**
 * FFNotesCreditCard — Section "Notes de crédit" d'une facture fournisseur.
 *
 * Permet d'ajouter une note de crédit partielle (réduit le reste dû sans
 * créer d'avoir comptable séparé). Repliable par défaut s'il y en a pas.
 */
import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FileType, Download, Trash2, ChevronDown, ChevronRight } from "lucide-react";

export default function FFNotesCreditCard({ f, id, currentUserRole, onAdd, onDelete }) {
  const [showForm, setShowForm] = useState(false);
  const [open, setOpen] = useState((f.notes_credit?.length || 0) > 0);
  const [form, setForm] = useState({ montant: "", commentaire: "", fichier: null });

  if (f.statut === "rejetee") return null;

  const submit = async () => {
    try {
      await onAdd(form);
      setShowForm(false);
      setForm({ montant: "", commentaire: "", fichier: null });
    } catch { /* déjà toasté */ }
  };

  const nbNc = f.notes_credit?.length || 0;
  const totalCredit = f.total_notes_credit || 0;

  return (
    <Card className="mt-4 border-purple-200 bg-purple-50/30" data-testid="ff-notes-credit-card">
      <div
        role="button"
        tabIndex={0}
        onClick={() => setOpen(!open)}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpen(!open); } }}
        className="w-full flex items-center justify-between p-3 hover:bg-purple-50 transition cursor-pointer"
      >
        <div className="flex items-center gap-2">
          {open ? <ChevronDown className="w-4 h-4 text-purple-600" /> : <ChevronRight className="w-4 h-4 text-purple-600" />}
          <FileType className="w-4 h-4 text-purple-600" />
          <span className="text-sm font-semibold text-gray-900">Notes de crédit</span>
          {nbNc > 0 && (
            <span className="bg-purple-200 text-purple-800 text-[10px] font-bold rounded-full px-2 py-0.5">
              {nbNc} · −{totalCredit.toFixed(2)} CHF
            </span>
          )}
        </div>
        {open && !showForm && (
          <Button
            size="sm" variant="outline"
            onClick={(e) => { e.stopPropagation(); setShowForm(true); }}
            className="border-purple-300 text-purple-700 hover:bg-purple-100 text-xs"
            data-testid="ff-add-nc-btn"
          >
            + Ajouter
          </Button>
        )}
      </div>

      {open && (
        <div className="px-3 pb-3 space-y-2">
          <p className="text-xs text-gray-500">
            Diminue simplement le reste dû — sans workflow d&apos;avoir comptable séparé.
          </p>

          {showForm && (
            <div className="bg-white p-3 rounded border border-purple-200 space-y-2" data-testid="ff-nc-form">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Montant (CHF) *</Label>
                  <Input
                    type="number" step="0.01" min="0" value={form.montant}
                    onChange={(e) => setForm({ ...form, montant: e.target.value })}
                    placeholder="ex: 78.00" className="font-mono text-right"
                    data-testid="ff-nc-montant"
                  />
                </div>
                <div>
                  <Label className="text-xs">Justificatif (PDF/image)</Label>
                  <Input
                    type="file" accept="application/pdf,image/*"
                    onChange={(e) => setForm({ ...form, fichier: e.target.files?.[0] || null })}
                    className="text-xs" data-testid="ff-nc-fichier"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs">Commentaire</Label>
                <Input
                  value={form.commentaire}
                  onChange={(e) => setForm({ ...form, commentaire: e.target.value })}
                  placeholder="Ex: Remise commerciale fin d'année"
                  data-testid="ff-nc-commentaire"
                />
              </div>
              <div className="flex gap-2 pt-1">
                <Button
                  variant="outline" size="sm" className="flex-1"
                  onClick={() => { setShowForm(false); setForm({ montant: "", commentaire: "", fichier: null }); }}
                >
                  Annuler
                </Button>
                <Button
                  size="sm" onClick={submit}
                  className="flex-[2] bg-purple-600 hover:bg-purple-700 text-white"
                  data-testid="ff-nc-submit-btn"
                >
                  Ajouter la note de crédit
                </Button>
              </div>
            </div>
          )}

          {nbNc > 0 ? (
            <div className="space-y-2">
              {f.notes_credit.map((nc) => (
                <div key={nc.id} className="flex items-center justify-between bg-white p-2.5 rounded border border-purple-100" data-testid={`ff-nc-item-${nc.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2">
                      <span className="font-mono font-bold text-purple-700">−{nc.montant.toFixed(2)} CHF</span>
                      <span className="text-[10px] text-gray-400">{nc.created_at?.slice(0, 10)}</span>
                    </div>
                    {nc.commentaire && <p className="text-xs text-gray-600 truncate">{nc.commentaire}</p>}
                    <p className="text-[10px] text-gray-400">par {nc.created_by_nom}</p>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    {nc.fichier && (
                      <a
                        href={`${process.env.REACT_APP_BACKEND_URL}/api/factures-fournisseurs/${id}/notes-credit/${nc.id}/file`}
                        target="_blank" rel="noreferrer"
                        className="p-1.5 text-gray-500 hover:text-purple-700"
                        title="Voir le justificatif"
                      >
                        <Download className="w-3.5 h-3.5" />
                      </a>
                    )}
                    {currentUserRole === "admin" && (
                      <Button
                        size="icon" variant="ghost"
                        onClick={() => onDelete(nc.id, nc.montant)}
                        className="h-7 w-7 text-red-500 hover:bg-red-50"
                        title="Supprimer (admin)"
                        data-testid={`ff-nc-delete-${nc.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
              <div className="flex items-center justify-between pt-2 border-t border-purple-200 text-xs">
                <span className="text-gray-600">Total crédité : <strong className="text-purple-700">−{totalCredit.toFixed(2)} CHF</strong></span>
                <span className="font-mono font-bold text-gray-900">Reste à payer : {(f.reste_a_payer ?? f.montant_ttc ?? 0).toFixed(2)} CHF</span>
              </div>
            </div>
          ) : (
            !showForm && <p className="text-xs text-gray-400 italic">Aucune note de crédit pour le moment.</p>
          )}
        </div>
      )}
    </Card>
  );
}
